import type {ReactElement} from 'react';
import {useMemo} from 'react';
import React, {type MouseEvent, useState} from 'react';
import type {UploadsMediaType} from '@Panels/user-media-panel/user-media-panel.types';
import {ControlledList} from '@Components/controlled-list';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import {Button, Size, Type} from '@Components/button';
import {noop} from '@Utils/general.util';
import {getItemsforAiOptionsDropdown} from '@Panels/user-media-panel/user-media.library';
import styles from './ai-options-dropdown.module.scss';

interface AiOptionsDropdownProps {
  mediaTypesToShow: UploadsMediaType[];
  onClick(listItemId: string, event?: MouseEvent): void;
  isSmallVersion?: boolean;
  selectorClasses?: string;
}

export default function AiOptionsDropdown({isSmallVersion = false, ...props}: AiOptionsDropdownProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const items = useMemo(() => {
    return getItemsforAiOptionsDropdown(props.mediaTypesToShow).map((item) => {
      return {...item, iconClasses: styles.aiIcon};
    });
  }, [props.mediaTypesToShow]);
  const getPopUp = (): ReactElement => {
    return <ControlledList onClick={props.onClick.bind(null)} items={items} />;
  };

  const getSelector = (): ReactElement => {
    return (
      <Button
        onClick={noop}
        key={'user-media-panel-ai-selector'}
        type={Type.SECONDARY}
        size={isSmallVersion ? Size.MEDIUM : Size.SMALL}
        icon={'icon-ai'}
        customClasses={`${isOpen ? styles.selectorActive : ''} ${isSmallVersion ? '_full-width' : ''} ${props.selectorClasses ?? ''}`}
        textClasses={`${styles.capitalize} ${styles.aiIcon}`}
        iconClassName={styles.aiIcon}
        text={window.i18next.t('pmwjs_generate')}
      />
    );
  };

  const getClassForHeight = (): string => {
    switch (items.length) {
      case 1:
        return styles.singleItem;
      case 2:
        return styles.twoItems;
      default:
        return '';
    }
  };

  return (
    <Dropdown
      onOpen={setIsOpen.bind(null, true)}
      onClose={setIsOpen.bind(null, false)}
      modalTitle={window.i18next.t('pmwjs_generate')}
      showModalForSmallScreen={isSmallVersion}
      popupSpacingClass="spacing-p-3"
      alignment={ALIGNMENT_TYPE.BOTTOM_START}
      popupClassName={`${styles.popup} ${getClassForHeight()}`}
      popUpHasCustomWidth
      popUpHasCustomHeight
      popupOffset={10.5}
      stopClickPropagation
      maxHeight={400}
      className={isSmallVersion ? '_full-width' : 'spacing-m-l-4'}
      selector={getSelector()}
      popup={getPopUp()}
      isResumable
    />
  );
}
